import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query'
import { ArrowLeft, BookOpen, GitBranch, Save, ShieldCheck, Sparkles, Trash2, UsersRound } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { chapterApi } from '@/api/chapterApi'
import { friendlyMessage } from '@/api/http'
import { genreTemplateApi } from '@/api/genreTemplateApi'
import { projectApi } from '@/api/projectApi'
import { AutopilotRunBar } from '@/components/autopilot/AutopilotRunBar'
import { ChapterTree } from '@/components/chapters/ChapterTree'
import { ChapterWorkspace } from '@/components/chapters/ChapterWorkspace'
import { ErrorBoundary } from '@/components/common/ErrorBoundary'
import { EntityRelationGraph } from '@/components/graph/EntityRelationGraph'
import { IngestReviewPanel } from '@/components/ingest/IngestReviewPanel'
import { CharacterPanel } from '@/components/project/CharacterPanel'
import { CascadePolicySettings } from '@/components/project/CascadePolicySettings'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { ContentModeSwitch } from '@/components/ContentModeSwitch'
import { ProjectBudgetPanel } from '@/components/project/ProjectBudgetPanel'
import { SceneLockPanel } from '@/components/project/SceneLockPanel'
import { ActBoundaryPanel } from '@/components/project/ActBoundaryPanel'
import { AssetLibraryPanel } from '@/components/project/AssetLibraryPanel'
import { CharacterStatePanel } from '@/components/project/CharacterStatePanel'
import { GameplayPanel } from '@/components/project/GameplayPanel'
import { ProductionPipelinePanel } from '@/components/project/ProductionPipelinePanel'
import { StorylinesPanel } from '@/components/project/StorylinesPanel'
import { StoryStatePanel } from '@/components/project/StoryStatePanel'
import {
  buildProjectSkillToggleBody,
  groupProjectSkillsByCategory,
  isProjectSkillToggleDisabled,
} from '@/components/project/projectSkillPanel'
import {
  buildProjectSettingsPatch,
  getProjectSettingsInitialValues,
  hasProjectSettingsChanges,
  resolveWorldRulesLabel,
  type ProjectSettingsFieldKey,
} from '@/components/project/projectSettingsPanel'
import {
  buildSoftConsistencyToggleBody,
  getSoftConsistencyChecked,
  getSoftConsistencyDescriptionKey,
  isSoftConsistencyControlDisabled,
  type SoftConsistencyToggleTarget,
} from '@/components/project/softConsistencyPanel'
import { SkeletonPlanner } from '@/components/skeleton/SkeletonPlanner'
import { Badge } from '@/components/ui/badge'
import { ProjectStatusBadge } from '@/components/project/ProjectStatusBadge'
import { Button } from '@/components/ui/button'
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { CONTENT_LANGUAGES, contentLanguageNative } from '@/lib/contentLanguages'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { resolveProjectGenreLabel } from '@/lib/projectGenreLabel'
import { useAuthStore } from '@/stores/authStore'
import { useUiStore } from '@/stores/uiStore'
import type { Asset, GenreTemplate, Project } from '@/types/nf'
import { formatCount, formatNumber } from '@/utils/format'
import { getSelectableChapterId } from '@/utils/chapterSelection'
import { getNextChapterNumber } from '@/utils/chapterGenerate'
import { chapterQualityFromAssets } from '@/components/quality/qualityGate'
import type { ProjectSkillSetting, ProjectSkillsStatus } from '@/api/projectApi'

function formatWords(
  current: number | null | undefined,
  target: number | null | undefined,
  labels: { generated: string; target: string; unit: string },
) {
  const generated = `${labels.generated} ${formatNumber(current ?? 0)} ${labels.unit}`
  return target ? `${generated} / ${labels.target} ${formatNumber(target)} ${labels.unit}` : generated
}

function MetaItem({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border bg-background/40 p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="nf-mono mt-1 text-sm text-foreground">{value}</p>
    </div>
  )
}

function DetailSkeleton() {
  return (
    <section className="px-4 py-6 sm:px-6 lg:px-8">
      <div className="mb-5 space-y-3">
        <Skeleton className="h-4 w-28" />
        <Skeleton className="h-9 w-80 max-w-full" />
        <Skeleton className="h-4 w-[520px] max-w-full" />
      </div>
      <div className="grid gap-4 lg:grid-cols-[280px_minmax(0,1fr)_320px]">
        <Skeleton className="h-[560px] rounded-lg" />
        <Skeleton className="h-[560px] rounded-lg" />
        <Skeleton className="h-[560px] rounded-lg" />
      </div>
    </section>
  )
}

function ProjectHeader({
  project,
  genreLabel,
  onDelete,
}: {
  project: Project
  genreLabel: string
  onDelete: () => void
}) {
  const { t } = useTranslation()
  const contentLanguageName = project.content_language
    ? t(`languageNames.${project.content_language}`, { defaultValue: contentLanguageNative(project.content_language) })
    : ''
  return (
    <div className="mb-5 flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
      <div>
        <Button asChild variant="ghost" size="sm" className="mb-3 -ml-2">
          <Link to="/projects">
            <ArrowLeft className="size-4" />
            {t('project.backToProjects')}
          </Link>
        </Button>
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-2xl font-semibold tracking-normal sm:text-3xl">{project.title}</h1>
          {project.status ? <ProjectStatusBadge status={project.status} /> : null}
          <Button asChild variant="outline" size="sm">
            <Link to={`/projects/${project.id}/branches`}>
              <GitBranch className="size-4" />
              {t('branch.title')}
            </Link>
          </Button>
          <Button asChild variant="default" size="sm">
            <Link to={`/projects/${project.id}/reader`}>
              <BookOpen className="size-4" />
              {t('reader.openReader')}
            </Link>
          </Button>
          <Button variant="outline" size="sm" className="text-destructive hover:text-destructive" onClick={onDelete}>
            <Trash2 className="size-4" />
            {t('project.delete')}
          </Button>
        </div>
        <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
          {project.description || t('project.noDescription')}
        </p>
      </div>
      <div className="grid grid-cols-2 gap-2 sm:flex">
        <MetaItem
          label={t('project.words')}
          value={formatWords(project.current_words, project.target_words, {
            generated: t('project.wordsGenerated'),
            target: t('project.wordsTarget'),
            unit: t('project.wordsUnit'),
          })}
        />
        <MetaItem label={t('project.chapters')} value={formatCount(project.chapter_count ?? 0)} />
        <MetaItem label={t('project.genre')} value={genreLabel || t('project.unknown')} />
        <MetaItem
          label={t('project.wizard.contentLanguage')}
          value={contentLanguageName || t('project.unknown')}
        />
      </div>
    </div>
  )
}

function ProjectSettingsField({
  id,
  label,
  value,
  disabled,
  multiline = false,
  onChange,
}: {
  id: string
  label: string
  value: string
  disabled: boolean
  multiline?: boolean
  onChange: (value: string) => void
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="text-xs text-muted-foreground">
        {label}
      </Label>
      {multiline ? (
        <textarea
          id={id}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          disabled={disabled}
          rows={3}
          className="min-h-20 w-full resize-y rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none transition-[color,box-shadow] placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-input/30"
        />
      ) : (
        <Input id={id} value={value} onChange={(event) => onChange(event.target.value)} disabled={disabled} />
      )}
    </div>
  )
}

function SoftConsistencyToggleRow({
  id,
  label,
  description,
  checked,
  disabled,
  experimental,
  onCheckedChange,
}: {
  id: string
  label: string
  description: string
  checked: boolean
  disabled: boolean
  experimental?: boolean
  onCheckedChange: (checked: boolean) => void
}) {
  const { t } = useTranslation()
  return (
    <div className="flex min-h-14 items-center justify-between gap-3 rounded-md border bg-background/40 px-3 py-2">
      <div className="min-w-0 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <Label htmlFor={id} className="text-sm font-medium">
            {label}
          </Label>
          {experimental ? (
            <Badge variant="outline" className="h-5 rounded-md px-1.5 text-xs font-normal">
              {t('detail.softConsistency.experimental')}
            </Badge>
          ) : null}
        </div>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      <Switch id={id} checked={checked} disabled={disabled} onCheckedChange={onCheckedChange} />
    </div>
  )
}

function ProjectSkillToggleRow({
  skill,
  disabled,
  onCheckedChange,
}: {
  skill: ProjectSkillSetting
  disabled: boolean
  onCheckedChange: (checked: boolean) => void
}) {
  const switchId = `project-skill-${encodeURIComponent(skill.name)}`
  return (
    <div className="flex min-h-16 items-center justify-between gap-3 rounded-md border bg-background/40 px-3 py-2">
      <div className="min-w-0 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <Label htmlFor={switchId} className="break-all text-sm font-medium">
            {skill.name}
          </Label>
          <Badge variant="outline" className="h-5 rounded-md px-1.5 text-xs font-normal">
            {skill.source}
          </Badge>
        </div>
        <p className="line-clamp-2 text-xs text-muted-foreground">{skill.description}</p>
      </div>
      <Switch
        id={switchId}
        checked={skill.enabled}
        disabled={disabled}
        onCheckedChange={onCheckedChange}
      />
    </div>
  )
}

export function ProjectSettingsPanel({
  project,
  genreTemplate,
  lang,
  projectQueryKey,
}: {
  project: Project
  genreTemplate: GenreTemplate | undefined
  lang: string
  projectQueryKey: QueryKey
}) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const contentMode = useUiStore((s) => s.contentMode)
  const initialValues = useMemo(() => getProjectSettingsInitialValues(project), [project])
  const [values, setValues] = useState(initialValues)

  // 只在切换项目(project.id 变)时重置表单; 同项目的后台 refetch(refetchOnWindowFocus/
  // 保存后 invalidate 等返回新 project 引用)不抹掉用户未保存编辑 (3-AI review B1).
  const lastProjectIdRef = useRef(project.id)
  useEffect(() => {
    if (lastProjectIdRef.current !== project.id) {
      lastProjectIdRef.current = project.id
      setValues(getProjectSettingsInitialValues(project))
    }
  }, [project])

  const patch = useMemo(() => buildProjectSettingsPatch(project, values), [project, values])
  const hasChanges = hasProjectSettingsChanges(project, values)
  const worldRulesLabel = resolveWorldRulesLabel(genreTemplate, lang, t('detail.worldRules'))
  const updateField = (key: ProjectSettingsFieldKey, value: string) => {
    setValues((current) => ({ ...current, [key]: value }))
  }
  const softConsistencyQueryKey = ['project-soft-consistency', project.id] as const
  const projectSkillsQueryKey = ['project-skills', project.id] as const

  const softConsistencyQuery = useQuery({
    queryKey: softConsistencyQueryKey,
    queryFn: () => projectApi.getSoftConsistency(project.id),
  })

  const projectSkillsQuery = useQuery({
    queryKey: projectSkillsQueryKey,
    queryFn: () => projectApi.getProjectSkills(project.id),
  })

  const saveMutation = useMutation({
    mutationFn: () => projectApi.patch(project.id, patch),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: projectQueryKey })
      toast.success(t('detail.settingsSaved'))
    },
    onError: (error) => {
      toast.error(friendlyMessage(error, t('detail.settingsSaveFailed')))
    },
  })

  const softConsistencyMutation = useMutation({
    mutationFn: ({ target, checked }: { target: SoftConsistencyToggleTarget; checked: boolean }) =>
      projectApi.updateSoftConsistency(project.id, buildSoftConsistencyToggleBody(target, checked)),
    onSuccess: (data) => {
      queryClient.setQueryData(softConsistencyQueryKey, data)
      toast.success(t('detail.softConsistency.saved'))
    },
    onError: (error) => {
      toast.error(friendlyMessage(error, t('detail.softConsistency.saveFailed')))
    },
  })

  const projectSkillMutation = useMutation({
    mutationFn: ({ name, checked }: { name: string; checked: boolean }) =>
      projectApi.updateProjectSkillEnabled(project.id, name, buildProjectSkillToggleBody(checked)),
    onSuccess: (updated) => {
      queryClient.setQueryData<ProjectSkillsStatus | undefined>(projectSkillsQueryKey, (current) => ({
        skills: (current?.skills ?? []).map((skill) => (skill.name === updated.name ? updated : skill)),
      }))
      toast.success(t('detail.projectSkills.saved'))
    },
    onError: (error) => {
      toast.error(friendlyMessage(error, t('detail.projectSkills.saveFailed')))
    },
  })

  const isSaving = saveMutation.isPending
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!hasChanges || isSaving) return
    saveMutation.mutate()
  }
  const softConsistencyDisabled = isSoftConsistencyControlDisabled(
    softConsistencyQuery.data,
    softConsistencyQuery.isLoading || softConsistencyMutation.isPending,
  )
  const projectSkillDisabled = isProjectSkillToggleDisabled(
    projectSkillsQuery.isLoading,
    projectSkillMutation.isPending,
  )
  const projectSkillGroups = groupProjectSkillsByCategory(projectSkillsQuery.data?.skills)
  const updateSoftConsistency = (target: SoftConsistencyToggleTarget, checked: boolean) => {
    if (softConsistencyDisabled) return
    softConsistencyMutation.mutate({ target, checked })
  }
  const updateProjectSkill = (name: string, checked: boolean) => {
    if (projectSkillDisabled) return
    projectSkillMutation.mutate({ name, checked })
  }

  return (
    <Card className="h-full min-h-[520px] rounded-lg py-0">
      <CardHeader className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <UsersRound className="size-4 text-primary" />
              {t('detail.settingsRoles')}
            </CardTitle>
            <CardDescription>{t('detail.settingsRolesHint')}</CardDescription>
          </div>
          <Button form="project-settings-form" type="submit" size="sm" disabled={!hasChanges || isSaving}>
            <Save className="size-4" />
            {isSaving ? t('detail.savingSettings') : t('common.save')}
          </Button>
        </div>
      </CardHeader>
      <Separator />
      <ScrollArea className="h-[460px]">
        <form id="project-settings-form" onSubmit={submit} className="space-y-4 p-4">
 <ProjectBudgetPanel key={project.id} project={project} />
          {contentMode === 'easy' ? (
            <>
              {/* 工位闸门。简易模式下置顶且不折叠 —— 它决定产出后是否自动进入
                  下一工位, 是用户主权所在, 藏起来就违背「工位间必须手动选择」。 */}
              <CascadePolicySettings projectId={project.id} simple />
              <Separator />
            </>
          ) : null}
          <ProjectSettingsField
            id="project-author"
            label={t('detail.author')}
            value={values.author}
            disabled={isSaving}
            onChange={(value) => updateField('author', value)}
          />
          <div>
            <p className="mb-2 text-xs font-medium uppercase text-muted-foreground">{t('detail.worldSettings')}</p>
            <div className="space-y-2 text-sm">
              {contentMode === 'easy' ? (
                <Collapsible>
                  <CollapsibleTrigger className="text-xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline">
                    {t('detail.worldExtras')}
                  </CollapsibleTrigger>
                  <CollapsibleContent className="space-y-2 pt-2">
                  <ProjectSettingsField
                    id="project-world-time-period"
                    label={t('detail.timePeriod')}
                    value={values.world_time_period}
                    disabled={isSaving}
                    onChange={(value) => updateField('world_time_period', value)}
                  />
                  <ProjectSettingsField
                    id="project-world-location"
                    label={t('detail.location')}
                    value={values.world_location}
                    disabled={isSaving}
                    onChange={(value) => updateField('world_location', value)}
                  />
                  <ProjectSettingsField
                    id="project-world-atmosphere"
                    label={t('detail.atmosphere')}
                    value={values.world_atmosphere}
                    disabled={isSaving}
                    onChange={(value) => updateField('world_atmosphere', value)}
                  />
                  </CollapsibleContent>
                </Collapsible>
              ) : (
                <>
              <ProjectSettingsField
                id="project-world-time-period"
                label={t('detail.timePeriod')}
                value={values.world_time_period}
                disabled={isSaving}
                onChange={(value) => updateField('world_time_period', value)}
              />
              <ProjectSettingsField
                id="project-world-location"
                label={t('detail.location')}
                value={values.world_location}
                disabled={isSaving}
                onChange={(value) => updateField('world_location', value)}
              />
              <ProjectSettingsField
                id="project-world-atmosphere"
                label={t('detail.atmosphere')}
                value={values.world_atmosphere}
                disabled={isSaving}
                onChange={(value) => updateField('world_atmosphere', value)}
              />
                </>
              )}
              <ProjectSettingsField
                id="project-world-rules"
                label={worldRulesLabel}
                value={values.world_rules}
                disabled={isSaving}
                multiline
                onChange={(value) => updateField('world_rules', value)}
              />
            </div>
          </div>
          {contentMode === 'easy' ? null : (
            <>
              <Separator />
              <CascadePolicySettings projectId={project.id} />
            </>
          )}
          <Separator />
          <div>
            <p className="mb-2 text-xs font-medium uppercase text-muted-foreground">{t('detail.projectDefinition')}</p>
            <div className="space-y-2 text-sm">
              <ProjectSettingsField
                id="project-core-theme"
                label={t('project.wizard.coreTheme')}
                value={values.core_theme}
                disabled={isSaving}
                multiline
                onChange={(value) => updateField('core_theme', value)}
              />
              <ProjectSettingsField
                id="project-logline"
                label={t('project.wizard.logline')}
                value={values.logline}
                disabled={isSaving}
                multiline
                onChange={(value) => updateField('logline', value)}
              />
              <ProjectSettingsField
                id="project-antagonist-source"
                label={t('project.wizard.antagonistSource')}
                value={values.antagonist_source}
                disabled={isSaving}
                onChange={(value) => updateField('antagonist_source', value)}
              />
              <ProjectSettingsField
                id="project-tone-keynote"
                label={t('project.wizard.toneKeynote')}
                value={values.tone_keynote}
                disabled={isSaving}
                onChange={(value) => updateField('tone_keynote', value)}
              />
              <ProjectSettingsField
                id="project-narrative-perspective"
                label={t('detail.narrativePerspective')}
                value={values.narrative_perspective}
                disabled={isSaving}
                onChange={(value) => updateField('narrative_perspective', value)}
              />
              <div className="space-y-1.5">
                <Label htmlFor="project-content-language">{t('project.wizard.contentLanguage')}</Label>
                <Select
                  value={values.content_language || undefined}
                  disabled={isSaving}
                  onValueChange={(value) => updateField('content_language', value)}
                >
                  <SelectTrigger id="project-content-language" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CONTENT_LANGUAGES.map((option) => (
                      <SelectItem key={option.code} value={option.code}>
                        {t(`languageNames.${option.code}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          {contentMode === 'easy' ? null : (
            <>
            <Separator />
            <div>
              <div className="mb-2 flex items-center justify-between gap-3">
                <p className="text-xs font-medium uppercase text-muted-foreground">
                  {t('detail.softConsistency.title')}
                </p>
                <ShieldCheck className="size-4 text-muted-foreground" />
              </div>
              <div className="space-y-2">
                {softConsistencyQuery.isError ? (
                  <p className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
                    {t('detail.softConsistency.loadFailed')}
                  </p>
                ) : null}
                {softConsistencyQuery.data && !softConsistencyQuery.data.globally_available ? (
                  <p className="rounded-md border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                    {t('detail.softConsistency.globallyDisabled')}
                  </p>
                ) : null}
                <SoftConsistencyToggleRow
                  id="project-soft-consistency-master"
                  label={t('detail.softConsistency.master')}
                  description={t(getSoftConsistencyDescriptionKey(softConsistencyQuery.data, 'master'))}
                  checked={getSoftConsistencyChecked(softConsistencyQuery.data, 'master')}
                  disabled={softConsistencyDisabled}
                  onCheckedChange={(checked) => updateSoftConsistency('master', checked)}
                />
                <SoftConsistencyToggleRow
                  id="project-soft-consistency-ooc"
                  label={t('detail.softConsistency.ooc')}
                  description={t(getSoftConsistencyDescriptionKey(softConsistencyQuery.data, 'ooc'))}
                  checked={getSoftConsistencyChecked(softConsistencyQuery.data, 'ooc')}
                  disabled={softConsistencyDisabled}
                  onCheckedChange={(checked) => updateSoftConsistency('ooc', checked)}
                />
                <SoftConsistencyToggleRow
                  id="project-soft-consistency-setting"
                  label={t('detail.softConsistency.setting')}
                  description={t(getSoftConsistencyDescriptionKey(softConsistencyQuery.data, 'setting'))}
                  checked={getSoftConsistencyChecked(softConsistencyQuery.data, 'setting')}
                  disabled={softConsistencyDisabled}
                  onCheckedChange={(checked) => updateSoftConsistency('setting', checked)}
                />
                <SoftConsistencyToggleRow
                  id="project-soft-consistency-emotion"
                  label={t('detail.softConsistency.emotion')}
                  description={t(getSoftConsistencyDescriptionKey(softConsistencyQuery.data, 'emotion'))}
                  checked={getSoftConsistencyChecked(softConsistencyQuery.data, 'emotion')}
                  disabled={softConsistencyDisabled}
                  experimental={softConsistencyQuery.data?.dimensions.emotion.experimental}
                  onCheckedChange={(checked) => updateSoftConsistency('emotion', checked)}
                />
              </div>
            </div>
            <Separator />
            <div>
              <div className="mb-2 flex items-center justify-between gap-3">
                <p className="text-xs font-medium uppercase text-muted-foreground">{t('detail.projectSkills.title')}</p>
                <Sparkles className="size-4 text-muted-foreground" />
              </div>
              <div className="space-y-3">
                {projectSkillsQuery.isError ? (
                  <p className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
                    {t('detail.projectSkills.loadFailed')}
                  </p>
                ) : null}
                {projectSkillsQuery.isLoading ? (
                  <div className="space-y-2">
                    <Skeleton className="h-16 rounded-md" />
                    <Skeleton className="h-16 rounded-md" />
                  </div>
                ) : null}
                {!projectSkillsQuery.isLoading && !projectSkillsQuery.isError && projectSkillGroups.length === 0 ? (
                  <p className="rounded-md border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                    {t('detail.projectSkills.empty')}
                  </p>
                ) : null}
                {projectSkillGroups.map((group) => (
                  <div key={group.category} className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground">{group.category}</p>
                    <div className="space-y-2">
                      {group.skills.map((skill) => (
                        <ProjectSkillToggleRow
                          key={skill.name}
                          skill={skill}
                          disabled={projectSkillDisabled}
                          onCheckedChange={(checked) => updateProjectSkill(skill.name, checked)}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            </>
          )}
          <Separator />
          <CharacterPanel project={project} />
          {contentMode === 'easy' ? null : <SceneLockPanel project={project} />}
        </form>
      </ScrollArea>
    </Card>
  )
}

const EASY_HIDDEN_TABS = ['gameplay', 'storylines', 'storyState', 'relationGraph']

export default function ProjectDetail() {
  const { id } = useParams()
	const [searchParams] = useSearchParams()
	const requestedTab = searchParams.get('tab')
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const user = useAuthStore((s) => s.user)
  const currentBranchId = useUiStore((s) => s.currentBranchId)
  const selectedChapterId = useUiStore((s) => s.selectedChapterId)
  const selectedChapterProjectId = useUiStore((s) => s.selectedChapterProjectId)
  const selectedChapterBranchId = useUiStore((s) => s.selectedChapterBranchId)
  const requestSelectChapter = useUiStore((s) => s.requestSelectChapter)
  const clearSelectedChapter = useUiStore((s) => s.clearSelectedChapter)
  const generatingChapter = useUiStore((s) => s.generatingChapter)
  const isGenerating = useUiStore((s) => s.isGenerating)
  const openChapterGenerateForm = useUiStore((s) => s.openChapterGenerateForm)
  const locale = useUiStore((s) => s.locale)
  const [activeTab, setActiveTab] = useState(() => requestedTab === 'pipeline' ? 'productionPipeline' : requestedTab === 'ingestReview' ? 'ingestReview' : 'chapters')
	useEffect(() => {
		setActiveTab(requestedTab === 'pipeline' ? 'productionPipeline' : requestedTab === 'ingestReview' ? 'ingestReview' : 'chapters')
	}, [id, requestedTab])
  const contentMode = useUiStore((s) => s.contentMode)
  // 简易档位隐藏纯专业页签: 调参(玩法)、长期规划(规划线)、只读诊断(故事状态)、图谱(关系图)。
  // 页签本身消失, 而不只是内容精简 —— 否则顶上仍是一排专业术语, 压迫感不减。
  const tabHidden = (value: string) => contentMode === 'easy' && EASY_HIDDEN_TABS.includes(value)
  // 切到简易时若正停在被隐藏的页签, 回落到「章节」, 避免出现空白主区。
  useEffect(() => {
    if (contentMode === 'easy' && EASY_HIDDEN_TABS.includes(activeTab)) setActiveTab('chapters')
  }, [activeTab, contentMode])
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)

  const projectQueryKey = ['project', user?.user_id, id, currentBranchId] as const

  const projectQuery = useQuery({
    queryKey: projectQueryKey,
    queryFn: () => projectApi.get(id!),
    enabled: Boolean(id),
  })

  const templatesQuery = useQuery({
    queryKey: ['genre-templates'],
    queryFn: genreTemplateApi.list,
  })

  const chaptersQuery = useQuery({
    queryKey: ['chapters', user?.user_id, id, currentBranchId],
    queryFn: () => chapterApi.list(id!, currentBranchId),
    enabled: Boolean(id),
  })
  const projectAssetsQuery = useQuery({
    queryKey: ['project-assets', id, currentBranchId],
    queryFn: () => projectApi.assets(id!),
    enabled: Boolean(id),
  })

  const deleteProjectMutation = useMutation({
    mutationFn: () => projectApi.delete(id!),
    onSuccess: async () => {
      toast.success(t('project.deleteMoved'))
      await queryClient.invalidateQueries({ queryKey: ['projects'] })
      await queryClient.invalidateQueries({ queryKey: ['projects-trash'] })
      navigate('/projects')
    },
    onError: (error) => toast.error(friendlyMessage(error, t('project.deleteFailed'))),
  })

  const chapters = chaptersQuery.data ?? []
  const qualityByChapterId = useMemo(() => {
    const assets = (projectAssetsQuery.data ?? []).filter((asset: Asset) => asset.branch_id === currentBranchId)
    return Object.fromEntries(chapters.map((chapter) => [chapter.id, chapterQualityFromAssets(chapter.id, assets)]))
  }, [chapters, currentBranchId, projectAssetsQuery.data])
  const scopedSelectedChapterId =
    selectedChapterProjectId === id && selectedChapterBranchId === currentBranchId
      ? getSelectableChapterId(chapters, selectedChapterId, id, currentBranchId)
      : null
  const selectedChapter = scopedSelectedChapterId
    ? chapters.find((chapter) => chapter.id === scopedSelectedChapterId) ?? null
    : null
	const pipelineChapter = selectedChapter ?? chapters.find((chapter) => chapter.status === 'done') ?? chapters[0] ?? null
  const scopedGeneratingChapter =
    generatingChapter && generatingChapter.projectId === id && generatingChapter.branchId === currentBranchId
      ? generatingChapter
      : null

  useEffect(() => {
    if (!selectedChapterId) return
    if (chaptersQuery.isLoading) return
    if (scopedSelectedChapterId) return
    clearSelectedChapter()
  }, [chaptersQuery.isLoading, clearSelectedChapter, scopedSelectedChapterId, selectedChapterId])

  if (projectQuery.isLoading) return <DetailSkeleton />

  if (projectQuery.error || !projectQuery.data) {
    return (
      <section className="grid min-h-[calc(100vh-3.5rem)] place-items-center px-4 py-8">
        <div className="max-w-md rounded-lg border bg-card p-6 text-center">
          <p className="font-semibold">{t('detail.loadFailed')}</p>
          <p className="mt-2 text-sm text-muted-foreground">
            {friendlyMessage(projectQuery.error, t('common.serviceError'))}
          </p>
          <Button className="mt-5" asChild>
            <Link to="/projects">{t('project.backToProjects')}</Link>
          </Button>
        </div>
      </section>
    )
  }

  const lang = i18n.resolvedLanguage ?? i18n.language
  const genreLabel = resolveProjectGenreLabel(projectQuery.data, templatesQuery.data, lang)
  const genreTemplate = templatesQuery.data?.find((item) => item.genre_key === projectQuery.data.genre)
  const handleSelectGraphEntity = (entityType: string, entityId: string) => {
    if (entityType === 'character') {
      setActiveTab('characterStates')
      return
    }
    if (entityType === 'chapter') {
      setActiveTab('chapters')
      const chapterNumber = Number(entityId)
      const chapter = chapters.find(
        (item) => item.id === entityId || (Number.isFinite(chapterNumber) && item.chapter_number === chapterNumber),
      )
      if (id && chapter) {
        requestSelectChapter({ projectId: id, branchId: currentBranchId, chapterId: chapter.id }, () =>
          window.confirm(t('chapter.generate.switchAwayConfirm')),
        )
      }
    }
  }

  return (
    <section className="px-4 py-6 sm:px-6 lg:px-8">
      <ProjectHeader project={projectQuery.data} genreLabel={genreLabel} onDelete={() => setDeleteConfirmOpen(true)} />
      <AutopilotRunBar
        projectId={id!}
        branchId={currentBranchId}
        chapters={chapters}
        contentLanguage={projectQuery.data.content_language ?? undefined}
        onEditOutline={() => setActiveTab('skeleton')}
      />
      <Tabs value={activeTab} onValueChange={setActiveTab} className="gap-4">
        <div className="flex flex-wrap items-center gap-3">
        <TabsList>
          <TabsTrigger value="chapters">{t('detail.chaptersTab')}</TabsTrigger>
          <TabsTrigger value="skeleton">{t('detail.skeletonTab')}</TabsTrigger>
          {tabHidden('gameplay') ? null : <TabsTrigger value="gameplay">{t('detail.gameplayTab')}</TabsTrigger>}
          <TabsTrigger value="productionPipeline">{t('detail.productionPipelineTab')}</TabsTrigger>
          <TabsTrigger value="ingestReview">{t('detail.ingestReviewTab')}</TabsTrigger>
          <TabsTrigger value="assetLibrary">{t('detail.assetLibraryTab')}</TabsTrigger>
          {tabHidden('storylines') ? null : <TabsTrigger value="storylines">{t('detail.storylinesTab')}</TabsTrigger>}
          {tabHidden('storyState') ? null : <TabsTrigger value="storyState">{t('detail.storyStateTab')}</TabsTrigger>}
          <TabsTrigger value="characterStates">{t('detail.characterStatesTab')}</TabsTrigger>
          {tabHidden('relationGraph') ? null : <TabsTrigger value="relationGraph">{t('detail.relationGraphTab')}</TabsTrigger>}
        </TabsList>
        {/* 全局创作档位。放 tab 行右侧, 项目下每个工位页签都能就近切换。 */}
        <ContentModeSwitch />
        </div>
        <TabsContent value="chapters">
          <div className="grid gap-4 lg:grid-cols-[280px_minmax(0,1fr)_320px]">
            <ChapterTree
              chapters={chapters}
              selectedChapterId={scopedGeneratingChapter ? null : scopedSelectedChapterId}
              isLoading={chaptersQuery.isLoading}
              error={chaptersQuery.error}
              isGenerating={Boolean(scopedGeneratingChapter && isGenerating)}
              qualityByChapterId={qualityByChapterId}
              onSelectChapter={(chapterId) => {
                if (!id) return
                requestSelectChapter({ projectId: id, branchId: currentBranchId, chapterId }, () =>
                  window.confirm(t('chapter.generate.switchAwayConfirm')),
                )
              }}
              onCreateChapter={() => {
                if (!id) return
                openChapterGenerateForm({
                  projectId: id,
                  branchId: currentBranchId,
                  chapterNumber: getNextChapterNumber(chapters),
                  outline: '',
                  title: '',
                  chapterMode: 'regular',
                  targetWords: 2500,
                  lang: projectQuery.data?.content_language ?? locale,
                })
              }}
            />
            <ErrorBoundary title={t('errors.boundary.chapterWorkspace')}>
              <ChapterWorkspace
                projectId={id!}
                branchId={currentBranchId}
                userId={user?.user_id}
                chapters={chapters}
                chapter={selectedChapter}
                quality={selectedChapter ? qualityByChapterId[selectedChapter.id] : null}
                contentLanguage={projectQuery.data?.content_language}
                onChapterUpdated={() => {
                  void chaptersQuery.refetch()
                  void projectAssetsQuery.refetch()
                  // 头部字数/章节数是后端实时聚合的, 章节一变就要重取。
                  // 注意: 【生成】路径不走这里(流式收尾在 ChapterStreamView →
                  // finalizeGeneratedChapter), 那条路径的失效已在那边处理。
                  // 这里覆盖的是重提交/局部改写等经过 ChapterWorkspace 的变更。
                  void projectQuery.refetch()
                }}
              />
            </ErrorBoundary>
            <ProjectSettingsPanel
              project={projectQuery.data}
              genreTemplate={genreTemplate}
              lang={lang}
              projectQueryKey={projectQueryKey}
            />
          </div>
        </TabsContent>
        <TabsContent value="skeleton">
          <div className="space-y-4">
            <SkeletonPlanner project={projectQuery.data} chapterCount={chapters.length} userId={user?.user_id} />
            {contentMode === 'easy' ? null : <ActBoundaryPanel project={projectQuery.data} branchId={currentBranchId} />}
          </div>
        </TabsContent>
        <TabsContent value="gameplay">
          <GameplayPanel key={`${id}:${currentBranchId}`} project={projectQuery.data} chapter={selectedChapter} branchId={currentBranchId} chapters={chapters} onSelectChapter={(chapterId) => {
            if (!id) return
            requestSelectChapter({ projectId: id, branchId: currentBranchId, chapterId }, () => window.confirm(t('chapter.generate.switchAwayConfirm')))
          }} />
        </TabsContent>
        <TabsContent value="productionPipeline">
		  {pipelineChapter ? (
		    <div className="mb-4 space-y-2">
		      <Label htmlFor="pipeline-chapter">{t('detail.pipelineViewer.chapter')}</Label>
		      <Select value={pipelineChapter.id} onValueChange={(chapterId) => {
		        if (id) requestSelectChapter({ projectId: id, branchId: currentBranchId, chapterId }, () => window.confirm(t('chapter.generate.switchAwayConfirm')))
		      }}>
		        <SelectTrigger id="pipeline-chapter" className="w-full"><SelectValue /></SelectTrigger>
		        <SelectContent>{chapters.map((chapter) => <SelectItem key={chapter.id} value={chapter.id}>{chapter.chapter_number} · {chapter.title}</SelectItem>)}</SelectContent>
		      </Select>
		    </div>
		  ) : null}
          <ProductionPipelinePanel project={projectQuery.data} chapter={pipelineChapter} simple={contentMode === 'easy'} />
        </TabsContent>
        <TabsContent value="ingestReview">
          <IngestReviewPanel project={projectQuery.data} branchId={currentBranchId} simple={contentMode === 'easy'} />
        </TabsContent>
        <TabsContent value="assetLibrary">
          <AssetLibraryPanel
            project={projectQuery.data}
            branchId={currentBranchId}
            simple={contentMode === 'easy'}
            onOpenExisting={(type) => setActiveTab(type === 'character' ? 'characterStates' : 'chapters')}
          />
        </TabsContent>
        <TabsContent value="storylines">
          <StorylinesPanel project={projectQuery.data} chapter={selectedChapter} />
        </TabsContent>
        <TabsContent value="storyState">
          <StoryStatePanel project={projectQuery.data} chapter={selectedChapter} />
        </TabsContent>
        <TabsContent value="characterStates">
          <CharacterStatePanel project={projectQuery.data} simple={contentMode === 'easy'} />
        </TabsContent>
        <TabsContent value="relationGraph">
          <EntityRelationGraph
            projectId={id!}
            branchId={currentBranchId}
            maxChapter={chapters.length}
            onSelectEntity={handleSelectGraphEntity}
          />
        </TabsContent>
      </Tabs>
      <Dialog open={deleteConfirmOpen} onOpenChange={(nextOpen) => {
        if (!nextOpen && !deleteProjectMutation.isPending) setDeleteConfirmOpen(false)
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('project.deleteConfirmTitle')}</DialogTitle>
            <DialogDescription>
              {t('project.deleteConfirmDescription', { title: projectQuery.data.title })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)} disabled={deleteProjectMutation.isPending}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteProjectMutation.mutate()}
              disabled={deleteProjectMutation.isPending}
            >
              <Trash2 className="size-4" />
              {t('project.moveToTrash')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  )
}
